<template>
  <div class="week-calendar" v-if="hasDays">
    <week-calendar-header
      :days="days"
      :now-moment="nowMoment"
    />
    <week-calendar-times
      :calendar-height="calendarHeight"
      :slot-height="slotHeight"
      :time-slots="timeSlots"
      :current-time-style="currentTimeStyle"
      :current-time-label="currentTimeLabel"
    />
    <div class="week-calendar-days">
      <week-calendar-day-column
        v-for="(day, index) in days"
        :key="`column-${day.date}-${index}`"
        :day="day"
        :calendar-height="calendarHeight"
        :slot-height="slotHeight"
        :time-slots="timeSlots"
        :event-groups="dayEventMap[day.date] || []"
        :current-time-style="currentTimeStyle"
        :can-drag-appointment="canDragAppointment"
        :is-dragging-appointment="isDraggingAppointment"
        :is-saving-appointment="isSavingAppointment"
        @event-pointerdown="onEventPointerDown"
        @deleteMultipleAppointments="onDeleteMultipleAppointments"
        @openModalInsuranceList="onOpenModalInsuranceList"
        @openModalExpenses="onOpenModalExpenses"
        @infoWaitingList="onInfoWaitingList"
      />
    </div>
  </div>
  <div v-else class="week-calendar-empty">
    <span>Nenhum dia disponível</span>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import WeekCalendarHeader from './WeekCalendarHeader.vue';
import WeekCalendarTimes from './WeekCalendarTimes.vue';
import WeekCalendarDayColumn from './WeekCalendarDayColumn.vue';
import calendarLayoutMixin from './calendarLayoutMixin';
import dragAndDropMixin from './dragAndDropMixin';

const MIN_MINUTE_HEIGHT = 4.5;
const MAX_MINUTE_HEIGHT = 12.0;
const MINUTE_HEIGHT_STEP = 0.5;

export default {
  name: 'WeekView',
  components: {
    WeekCalendarHeader,
    WeekCalendarTimes,
    WeekCalendarDayColumn,
  },
  mixins: [calendarLayoutMixin, dragAndDropMixin],
  props: {
    days: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: null,
      timeUpdateInterval: null,
      secondUpdateInterval: null,
      wheelHandler: null,
    };
  },
  computed: {
    ...mapState({
      nowState: ({ now }) => now,
    }),
    nowMoment() {
      if (this.currentTime) {
        return moment(this.currentTime);
      }
      return this.nowState ? moment(this.nowState) : moment();
    },
    currentTimeLabel() {
      return this.nowMoment.format('HH:mm');
    },
    hasDays() {
      return Array.isArray(this.days) && this.days.length > 0;
    },
  },
  methods: {
    onDeleteMultipleAppointments(payload) {
      this.$emit('deleteMultipleAppointments', payload);
    },
    onOpenModalInsuranceList(payload) {
      this.$emit('openModalInsuranceList', payload);
    },
    onOpenModalExpenses(payload) {
      this.$emit('openModalExpenses', payload);
    },
    onInfoWaitingList() {
      this.$emit('infoWaitingList');
    },
    updateCurrentTime() {
      this.currentTime = new Date();
    },
    scrollToCurrentTime() {
      this.$nextTick(() => {
        if (!this.$el || !this.currentTimeStyle) {
          return;
        }

        const currentTimeElement = this.$el.querySelector('.week-calendar-current-time');
        if (!currentTimeElement) {
          return;
        }

        try {
          currentTimeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        } catch (e) {
          const elementRect = currentTimeElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle = absoluteElementTop - (window.innerHeight / 2);
          window.scrollTo({
            top: Math.max(0, middle),
            behavior: 'smooth',
          });
        }
      });
    },
    handleWheel(event) {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const { deltaY } = event;
        if (deltaY !== 0) {
          const direction = deltaY > 0 ? -1 : 1;
          const newMinuteHeight = this.minuteHeight + (direction * MINUTE_HEIGHT_STEP);
          if (newMinuteHeight >= MIN_MINUTE_HEIGHT && newMinuteHeight <= MAX_MINUTE_HEIGHT) {
            this.minuteHeight = newMinuteHeight;
          }
        }
      }
    },
  },
  mounted() {
    this.updateCurrentTime();
    this.timeUpdateInterval = setInterval(() => {
      this.updateCurrentTime();
    }, 60000);
    this.secondUpdateInterval = setInterval(() => {
      this.updateCurrentTime();
    }, 1000);

    this.wheelHandler = this.handleWheel.bind(this);
    window.addEventListener('wheel', this.wheelHandler, { passive: false });

    if (!this.loading && this.hasDays) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToCurrentTime();
        }, 1000);
      });
    }
  },
  beforeDestroy() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
    if (this.secondUpdateInterval) {
      clearInterval(this.secondUpdateInterval);
      this.secondUpdateInterval = null;
    }
    if (this.wheelHandler) {
      window.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
  },
  watch: {
    days: {
      handler(newDays, oldDays) {
        if (newDays && newDays.length > 0 && (!oldDays || oldDays.length === 0)) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.scrollToCurrentTime();
            }, 1200);
          });
        }
      },
      immediate: false,
    },
    hasDays: {
      handler(newValue, oldValue) {
        if (newValue && !oldValue && this.currentTimeStyle) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.scrollToCurrentTime();
            }, 1200);
          });
        }
      },
      immediate: false,
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';

.week-calendar {
  display: grid;
  grid-template-columns: 50px 1fr;
  grid-template-rows: auto 1fr;
}

.week-calendar-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  position: relative;
  width: 100%;
  grid-column: 2 / 3;
  grid-row: 2 / 3;
  border-right: $border-width solid $border-color;
  border-bottom: $border-width solid $border-color;
}

.week-calendar-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $layout-spacing-lg;
  color: $gray-color;
  font-size: $font-size-sm;
}

@media screen and (max-width: $size-lg) {
  .week-calendar-body {
    grid-template-columns: 3.5rem 1fr;
  }
}
</style>
